/**
 * AnimatedCounter — Counts from 0 to a target number on mount.
 *
 * Uses requestAnimationFrame for smooth 60fps counting.
 * Respects reduced-motion preference (shows final value immediately).
 *
 * @see documents/ui-enhancement-spec.md §9.3 (Score & Data Visualisation)
 */

import React, { useEffect, useState, useRef } from 'react';
import { Text } from '@shopify/polaris';
import type { TextProps } from '@shopify/polaris';
import { useZeliumTheme } from '../../theme';

interface AnimatedCounterProps {
  /** Target value to count to */
  value: number;
  /** Duration of the count animation in seconds (default: motion.duration.dramatic) */
  duration?: number;
  /** Text variant (Polaris) */
  variant?: TextProps['variant'];
  /** Text weight */
  fontWeight?: TextProps['fontWeight'];
  /** Optional suffix (e.g., '%', ' points') */
  suffix?: string;
  /** Optional prefix (e.g., '+') */
  prefix?: string;
  /** Custom inline style for the text */
  style?: React.CSSProperties;
  /** Text tone */
  tone?: TextProps['tone'];
}

export function AnimatedCounter({
  value,
  duration: durationOverride,
  variant = 'headingXl',
  fontWeight = 'bold',
  suffix = '',
  prefix = '',
  style,
  tone,
}: AnimatedCounterProps): React.ReactElement {
  const { reducedMotion, motion } = useZeliumTheme();
  const effectiveDuration = durationOverride ?? motion.duration.dramatic;
  const [displayed, setDisplayed] = useState(reducedMotion ? value : 0);
  const startTime = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    if (reducedMotion || effectiveDuration === 0) {
      setDisplayed(value);
      return;
    }

    startTime.current = null;

    const animate = (timestamp: number) => {
      if (startTime.current === null) startTime.current = timestamp;
      const elapsed = (timestamp - startTime.current) / 1000; // seconds
      const progress = Math.min(elapsed / effectiveDuration, 1);

      // Ease-out deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(eased * value));

      if (progress < 1) {
        rafId.current = requestAnimationFrame(animate);
      }
    };

    rafId.current = requestAnimationFrame(animate);
    return () => {
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, [value, effectiveDuration, reducedMotion]);

  return (
    <Text as="span" variant={variant} fontWeight={fontWeight} tone={tone}>
      <span style={style}>
        {prefix}{displayed}{suffix}
      </span>
    </Text>
  );
}
